import { Box, Stack, Typography } from '@mui/material';

import { useAppSelector } from 'state/hooks';
import { selectCurrentProjectInfo } from 'state/workflowSlice';

import { PROCESS_STATUS } from 'appConstants';
import { Scenario, StlCard } from 'appTypes';
import { convertTo12Hour } from 'features/common/DateTimeSelector/utils';
import { SegmentSvg } from 'features/common/SegmentSvg';
import StlItemCard from 'features/common/StlItemCard/StlItemCard';
import { formatHoursToHourMin } from 'features/common/datetimeUtils';
import { formatDistance } from 'features/common/utils';

import { useScenarioStatus } from '../DetourScenarioStatusProvider';
import ScenarioCreateButtons from '../ScenarioCreateButtons';
import { useScenarioUI } from '../hooks/useScenarioUI';
import { closureDurationHours } from '../utils';
import ScenarioCardMenu from './ScenarioCardMenu';

import styles from './ScenarioCard.module.css';

export default function ScenarioCard({
  item: scenario,
  selected,
  fullWidth,
}: StlCard<Scenario>) {
  if (!scenario) return null;

  const userProject = useAppSelector(selectCurrentProjectInfo);
  const { setEditMode, setCopyMode } = useScenarioUI();
  const status = useScenarioStatus(scenario);

  const {
    scenario_name,
    days_of_week,
    year_month,
    closure_start,
    closure_end,
    new_capacities,
    length,
    uuid,
  } = scenario;

  const startDay = days_of_week?.[0] ?? undefined;
  const endDay = days_of_week?.at(-1) ?? undefined;
  const closure = `${new_capacities?.[0] ?? 'Unknown '}%`;

  const dist = formatDistance(length, userProject.uses_metric);
  const durationInH =
    selected || fullWidth
      ? formatHoursToHourMin(
          closureDurationHours(startDay, endDay, closure_start, closure_end),
        )
      : undefined;

  return (
    <Stack display="flex" width="100%">
      <StlItemCard<Scenario>
        itemType="scenario"
        status={status}
        item={scenario}
        selected={selected}
        fullWidth={fullWidth}
        img={fullWidth && <SegmentSvg type="scenario" id={uuid} />}
        secondaryLine={
          fullWidth
            ? `Closure Distance: ${dist} • Remaining Capacity: ${closure}`
            : `${dist} • ${closure} Capacity`
        }
        name={scenario_name}
        setEditMode={setEditMode}
        setCopyMode={setCopyMode}
        menu={<ScenarioCardMenu scenario={scenario} scenarioStatus={status} />}
      >
        {selected && (
          <Box display="flex" data-selected="true" data-fullwidth={fullWidth}>
            <Box className={styles.time_duration_inner_box}>
              <Typography variant="caption" className={styles.op8}>
                Distance
              </Typography>
              <Typography variant="caption" className={styles.txt_data}>
                {dist}
              </Typography>
            </Box>
            <Box className={styles.time_duration_inner_box}>
              <Typography variant="caption" className={styles.op8}>
                Capacity
              </Typography>
              <Typography variant="caption" className={styles.txt_data}>
                {closure}
              </Typography>
            </Box>
          </Box>
        )}
        <Box
          className={styles.time_duration_box}
          data-selected={selected}
          data-fullwidth={fullWidth}
        >
          <Box className={styles.time_duration_inner_box} flex={1}>
            <Typography variant="caption" className={styles.op8}>
              Time Period
            </Typography>
            <Typography variant="caption" className={styles.txt_data}>
              {year_month?.replace('-', ' ')}
            </Typography>
          </Box>
          <Box className={styles.time_duration_inner_box} flex={2}>
            <Typography variant="caption" className={styles.op8}>
              Duration
            </Typography>
            <Typography variant="caption" className={styles.txt_data}>
              {closure_start &&
                startDay &&
                `${startDay.slice(0, 3)}, ${convertTo12Hour(closure_start)}`}
              {!fullWidth && !selected && <br />} to{' '}
              {closure_end &&
                endDay &&
                `${endDay.slice(0, 3)}, ${convertTo12Hour(closure_end)}`}
              {durationInH && ` (${durationInH})`}
            </Typography>
          </Box>
        </Box>
      </StlItemCard>
      {selected && status === PROCESS_STATUS.PROCESSING && (
        <ScenarioCreateButtons />
      )}
    </Stack>
  );
}
